export type CompanyMailboxSummary = {
  _id?: string;
  email?: string;
  mailType?: string;
  isDefault?: boolean;
  googleId?: string;
};

export type ActiveCompanyMailbox = {
  companyId: string;
  mailboxId?: string;
  email: string;
  mailType: string;
  accessToken?: string;
  googleId?: string;
  scopes?: string[] | string;
};

export const resolveCompanyId = (profile: unknown): string | null => {
  if (!profile || typeof profile !== 'object') return null;
  const record = profile as Record<string, unknown>;
  const id = record._id || record.id || record.companyId;
  return id ? String(id) : null;
};

export const mailboxesQueryKey = (companyId: string | null | undefined) =>
  companyId ? (['mailboxes', companyId] as const) : (['mailboxes'] as const);

export const pickCompanyMailbox = (
  mailboxes: CompanyMailboxSummary[] | undefined | null,
  preferred?: { mailboxId?: string; email?: string } | null
): CompanyMailboxSummary | null => {
  if (!Array.isArray(mailboxes) || mailboxes.length === 0) {
    return null;
  }
  if (preferred?.mailboxId) {
    const byId = mailboxes.find((mailbox) => String(mailbox._id) === String(preferred.mailboxId));
    if (byId) return byId;
  }
  if (preferred?.email) {
    const wanted = preferred.email.toLowerCase();
    const byEmail = mailboxes.find(
      (mailbox) => String(mailbox.email || '').toLowerCase() === wanted
    );
    if (byEmail) return byEmail;
  }
  return mailboxes.find((mailbox) => mailbox.isDefault) || mailboxes[0];
};

export const buildActiveCompanyMailbox = (
  companyId: string,
  mailbox: CompanyMailboxSummary,
  extras?: Partial<ActiveCompanyMailbox>
): ActiveCompanyMailbox => ({
  companyId: String(companyId),
  mailboxId: mailbox._id ? String(mailbox._id) : extras?.mailboxId,
  email: String(mailbox.email || extras?.email || ''),
  mailType: String(mailbox.mailType || extras?.mailType || 'google'),
  accessToken: extras?.accessToken,
  googleId: extras?.googleId || mailbox.googleId,
  scopes: extras?.scopes,
});

export const isCompanyMailboxActiveFor = (
  active: ActiveCompanyMailbox | null | undefined,
  companyId: string | null | undefined
): boolean =>
  Boolean(active && companyId && String(active.companyId) === String(companyId) && active.email);

/** True when the user explicitly opened their personal inbox while a company profile is selected. */
export const isPreferPersonalMailbox = (
  mailState: { preferPersonalMailbox?: boolean } | null | undefined
): boolean => Boolean(mailState?.preferPersonalMailbox);

/**
 * Company profile is selected, but mail should use the user's personal credentials
 * (explicit prefer-personal mode, or no active company mailbox).
 */
export const shouldUsePersonalMailboxInCompany = (
  companyId: string | null | undefined,
  active: ActiveCompanyMailbox | null | undefined,
  preferPersonal: boolean
): boolean => {
  if (!companyId) return true;
  if (preferPersonal) return true;
  return !isCompanyMailboxActiveFor(active, companyId);
};
